import {Metadata} from "../models/remoteConstants.model";
import {AttributeManifestEntry} from "../models/attributeManifest.model";

export interface MetadataDisplayInfo {
    suffix: string;
    tooltip: string | undefined;
}

// Bracketed summary of a metadata's authored parameters, for compact labels —
// e.g. a SpawnUnitUpgrade reward reads "Spawn [Giant]". Uses the manifest entry
// (when present) to order fields like the form does and to map enum integers to
// member names; entity references are localized to their display name. Enum
// params always show (falling back to the default member when unauthored) so an
// Improvement reward reads "Improvement [Walls]" even before it's edited;
// numeric params appear only when authored. Returns '' when there is nothing to
// show.
export function getParameterSuffix(
    meta: { values?: { [key: string]: number }, entities?: { [key: string]: string } } | undefined,
    manifestEntry: AttributeManifestEntry | undefined,
    getLocalizationValue: (key: string) => string
): string {
    if (!meta) return '';
    const parts: string[] = [];

    if (manifestEntry) {
        for (const p of manifestEntry.Parameters) {
            if (p.Type === 'enum' && p.EnumValues) {
                const idx = (meta.values?.[p.Name] ?? p.DefaultValue) as number;
                if (p.EnumValues[idx] !== undefined) parts.push(p.EnumValues[idx]);
                continue;
            }
            const v = meta.values?.[p.Name];
            if (v === undefined) continue;
            parts.push(String(v));
        }
        for (const e of manifestEntry.Entities) {
            const v = meta.entities?.[e.Name];
            if (v) parts.push(getLocalizationValue(v));
        }
    } else {
        for (const v of Object.values(meta.values ?? {})) parts.push(String(v));
        for (const v of Object.values(meta.entities ?? {})) if (v) parts.push(getLocalizationValue(v));
    }

    return parts.length ? ' [' + parts.join(', ') + ']' : '';
}

export function getMetadataDisplayInfo(
    meta: Metadata | undefined,
    isDuplicate: boolean,
    getLocalizationValue: (key: string) => string
): MetadataDisplayInfo {
    const valueEntries = meta?.values ? Object.entries(meta.values) : [];

    const suffix = isDuplicate && valueEntries.length > 0
        ? ' [' + valueEntries.map(([, v]) => v).join(', ') + ']'
        : '';

    const desc = meta?.id ? getLocalizationValue(`${meta.id}.description`) : '';
    const descDisplay = desc !== `${meta?.id}.description` ? desc : ''; // don't show raw key fallback
    const paramLines = valueEntries.map(([k, v]) => `${k}: ${v}`).join('\n');
    const tooltip = [descDisplay, paramLines].filter(s => s).join('\n\n') || undefined;

    return { suffix, tooltip };
}

export function getDuplicateIds(allMetadata: Metadata[]): Set<string> {
    const counts = new Map<string, number>();
    for (const m of allMetadata) {
        counts.set(m.id, (counts.get(m.id) ?? 0) + 1);
    }
    const dupes = new Set<string>();
    counts.forEach((count, id) => {
        if (count > 1) dupes.add(id);
    });
    return dupes;
}